import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Project } from "@/javascript/project-objects";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Pagination,
    ScrollArea,
    Skeleton,
    Table,
    Text
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

interface ClassroomProjectsBrowserProps {
    classroom: Classroom;
    onClose: () => void;
    onProjectSelected: () => void;
}

const ClassroomProjectsBrowser: React.FC<ClassroomProjectsBrowserProps> = ({ classroom, onClose, onProjectSelected }) => {
    const [projects, setProjects] = useState<Project[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [projectsPerPage, setProjectsPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("This component must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const height = 60; // view height (vh)
    const calculateProjectsPerPage = useCallback(() => {
        const rowHeight = 45; // pixel estimation
        const containerHeight = window.innerHeight * (height / 100) - 100; // Use 60% of the viewport height minus some estimate for other elements
        setProjectsPerPage(Math.floor(containerHeight / rowHeight));

        if (projects && Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > projects.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, window.innerHeight]);

    useEffect(() => {
        calculateProjectsPerPage();
        window.addEventListener("resize", calculateProjectsPerPage);
        return () => window.removeEventListener("resize", calculateProjectsPerPage);
    }, [calculateProjectsPerPage]);

    useEffect(() => {
        const notificationId = Utils.displayNotification(
            "Loading Projects",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.getClassroomProjects(classroom.id, token)
            .then((loadedProjectsInfo: Project[] | null) => {
                if (!loadedProjectsInfo || loadedProjectsInfo.length <= 0) {
                    setProjects(null);
                    Utils.updateNotification(
                        notificationId,
                        "No Projects Found",
                        undefined,
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                } else {
                    setProjects(
                        loadedProjectsInfo.sort(
                            (a, b) =>
                                new Date(b.getLastEditedDate()).getTime() - new Date(a.getLastEditedDate()).getTime()
                        )
                    );
                    Utils.updateNotification(
                        notificationId,
                        "Projects Loaded",
                        undefined,
                        "green",
                        <IconCheck size={16} />
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Projects",
                    "An error occurred while retrieving classroom projects.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }, [classroom.id, token]);

    const getLatestSubmissionDate = (project: Project): string => {
        if (!project.submissions || project.submissions.length === 0) {
            return "Not Submitted";
        }
        const latestSubmission = [...project.submissions].sort(
            (a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime()
        )[0];
        return formatDateString(latestSubmission.dateSubmitted);
    };

    const paginatedProjects = useMemo(() => {
        const start = (desiredPage - 1) * projectsPerPage;
        return projects?.slice(start, start + projectsPerPage) ?? [];
    }, [desiredPage, projects, projectsPerPage]);

    return (
        <ConductionModal
            onClose={onClose}
        >
            <Flex
                direction={"column"}
                align="center"
                justify="space-between"
                style={{
                    height: `${height}vh`
                }}
            >
                {loading ? (
                    <Skeleton height={"100%"} radius="md" />
                ) : projects === null ? (
                    <Text>No projects found.</Text>
                ) : (
                    <ScrollArea
                        style={{
                            width: "100%"
                        }}
                    >
                        <Table
                            striped
                            stickyHeader
                            highlightOnHover
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Name</Table.Th>
                                    <Table.Th>Creator</Table.Th>
                                    <Table.Th>Created</Table.Th>
                                    <Table.Th>Last Edited</Table.Th>
                                    <Table.Th>Submitted</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedProjects.map((project) => (
                                    <Table.Tr
                                        key={project.getId()}
                                    >
                                        <Table.Td>{project.getName()}</Table.Td>
                                        <Table.Td>{project.getUser()?.username ?? ""}</Table.Td>
                                        <Table.Td><Badge color="blue">{formatDateString(project.getCreatedDate())}</Badge></Table.Td>
                                        <Table.Td><Badge color="blue">{formatDateString(project.getLastEditedDate())}</Badge></Table.Td>
                                        <Table.Td><Badge color={project.submissions.length <= 0 ? "red" : "darkGreen"}>{getLatestSubmissionDate(project)}</Badge></Table.Td>
                                        <Table.Td>
                                            <Button
                                                size="xs"
                                                onClick={() => {
                                                    auth.openProject(project.getId());
                                                    onProjectSelected();
                                                    onClose();
                                                }}
                                            >
                                                Load Project
                                            </Button>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                )}

                {projects && projects.length > projectsPerPage && (
                    <Pagination
                        total={Math.ceil(projects.length / projectsPerPage)}
                        value={desiredPage}
                        onChange={setDesiredPage}
                        style={{
                            paddingTop: 20
                        }}
                    />
                )}
            </Flex>
        </ConductionModal>
    );
};

function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate);
    return date.toLocaleDateString();
}

export default ClassroomProjectsBrowser;
